import { useState } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert, Box, Button, Card, CardContent, CircularProgress, Container, Link, Stack,
  TextField, Typography,
} from '@mui/material';
import { api } from '../api/client';
import { useAuthStore } from '../stores/auth';
import type { AcceptInviteResponse } from '../api/types';

/**
 * Landing page for the link in the password-reset email. The token comes from
 * the query string (set by the email). On success we auto-log the user in,
 * matching the signup / accept-invite UX so they don't have to re-enter
 * credentials right after picking them.
 */
export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const setSession = useAuthStore((s) => s.setSession);
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  if (!token) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', bgcolor: 'background.default' }}>
        <Container maxWidth="xs">
          <Card>
            <CardContent>
              <Alert severity="error">Missing reset token. Use the link from your email.</Alert>
              <Typography variant="body2" align="center" color="text.secondary" sx={{ mt: 2 }}>
                <Link component={RouterLink} to="/forgot-password">Request a new link</Link>
              </Typography>
            </CardContent>
          </Card>
        </Container>
      </Box>
    );
  }

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (password !== confirm) {
      setErr('Passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post<AcceptInviteResponse>('/api/auth/reset-password', {
        token, newPassword: password,
      });
      if (data.autoLogin && data.token && data.user) {
        setSession(data.token, data.user);
        navigate('/home', { replace: true });
      } else {
        navigate('/login', { replace: true });
      }
    } catch (ex: unknown) {
      const code = (ex as { response?: { data?: { error?: string } } })?.response?.data?.error;
      const msg = code === 'expired' ? 'This link has expired. Request a new one.'
                : code === 'already-used' ? 'This link has already been used.'
                : code === 'invalid-token' ? 'This link is no longer valid.'
                : code ?? 'Password reset failed';
      setErr(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', bgcolor: 'background.default' }}>
      <Container maxWidth="xs">
        <Card>
          <CardContent>
            <Typography variant="h5" sx={{ mb: 0.5, fontWeight: 700 }}>Set a new password</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Pick a new password for your account. We'll sign you in afterwards.
            </Typography>
            <Stack component="form" onSubmit={onSubmit} spacing={2}>
              <TextField
                label="New password" type="password" value={password}
                onChange={(e) => setPassword(e.target.value)} required size="small"
                autoComplete="new-password" autoFocus helperText="At least 8 characters."
              />
              <TextField
                label="Confirm password" type="password" value={confirm}
                onChange={(e) => setConfirm(e.target.value)} required size="small"
                autoComplete="new-password"
              />
              {err && <Alert severity="error" variant="outlined">{err}</Alert>}
              <Button type="submit" variant="contained" disabled={busy} size="large"
                      startIcon={busy ? <CircularProgress size={18} /> : null}>
                {busy ? 'Updating…' : 'Reset password'}
              </Button>
              <Typography variant="body2" align="center" color="text.secondary">
                <Link component={RouterLink} to="/login">Back to sign in</Link>
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}
